"use client";

import * as React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, CircleQuestionMark as HelpCircle } from 'lucide-react';
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const FAQ_ITEMS: FaqItem[] = [{
  id: "item-1",
  question: "How does permitting differ between the City of Decatur and unincorporated DeKalb County?",
  answer: "Properties inside the four-square-mile City of Decatur municipal boundary submit building, plumbing, mechanical, and electrical permit applications directly to Decatur City Hall on North McDonough Street. Properties in neighboring unincorporated DeKalb County submit through the DeKalb County Department of Planning and Sustainability. Decatur municipal review includes distinct zoning checks, high-performance building standards, and local stormwater requirements that differ from the county system."
}, {
  id: "item-2",
  question: "When is Historic District Design Review required for exterior remodeling?",
  answer: "Exterior renovations, window replacements, roofline alterations, and additions on homes within locally designated Decatur historic districts (such as McDonough-Adams-Kings Highway or Clairemont Avenue) require a Certificate of Appropriateness (COA) from the Decatur Historic Preservation Commission prior to permit issuance. Interior remodeling projects on designated homes do not require historic commission approval."
}, {
  id: "item-3",
  question: "What structural factors arise when remodeling 1920s Craftsman bungalows?",
  answer: "Early 20th-century bungalows frequently feature dimensional rough-sawn lumber, heart pine floor joists, unreinforced brick pier foundations, and load-bearing central spine walls. Altering these layouts for open living spaces requires careful structural evaluation, sistering aged joists, and installing engineered LVL or steel beams to support existing roof geometries."
}, {
  id: "item-4",
  question: "How are plaster walls and vintage lath addressed during room overhauls?",
  answer: "Original plaster over wood or metal lath provides substantial acoustic mass but complicates modern wire, duct, and plumbing chases. Depending on condition, rooms may undergo localized channel patching or systematic selective removal down to the studs to allow new insulation, square corners for custom cabinetry, and modern low-voltage integration."
}, {
  id: "item-5",
  question: "What is the typical sequence for a major interior renovation?",
  answer: "Projects begin with site protection and dust containment, followed by selective demolition and structural framing. Rough trades—plumbing, HVAC, and electrical—are installed and inspected before insulation and drywall. Finishes then follow a clear hierarchy: interior trim and cabinetry, wall finishes, tile surfaces, flooring installation, fixture trimming, and final mechanical commissioning."
}, {
  id: "item-6",
  question: "How do Decatur's tree protection ordinances affect addition plans?",
  answer: "Decatur enforces a stringent tree canopy ordinance. Any footprint expansion, patio addition, or heavy equipment access route that encroaches into the Critical Root Zone (CRZ) of protected canopy trees requires a tree preservation plan, boundary fencing, and in some cases canopy mitigation fees before site disturbance permits are granted."
}, {
  id: "item-7",
  question: "Can original hardwood flooring be blended during floor plan reconfigurations?",
  answer: "Historic Decatur homes predominantly feature 2-1/4 inch or 3-1/4 inch solid white oak, red oak, or heart pine. When removing walls or extending rooms, matching tongue-and-groove boards are laced directly into the existing pattern, followed by complete room sanding and oil-based or waterborne finishing to achieve uniform color and sheen."
}, {
  id: "item-8",
  question: "What infrastructure upgrades are standard when modernizing 1950s brick ranches?",
  answer: "Mid-century ranch homes commonly require upgrading electrical service to 200 amps, replacing original galvanized supply lines or cast iron drain stacks with modern copper and PVC, and reconfiguring low-pitch attic ductwork with sealed high-efficiency heat pump systems."
}, {
  id: "item-9",
  question: "How are ceiling heights and roof pitches managed during attic conversions?",
  answer: "Converting second-story attic spaces into livable master suites or bedrooms requires meeting modern building codes for minimum 7-foot ceiling heights across the usable floor area. Dormers—either shed or gable configurations—are engineered to gain headroom, allow code-compliant egress windows, and preserve the exterior roofline proportions."
}, {
  id: "item-10",
  question: "How is dust containment handled in occupied residences during construction?",
  answer: "Living areas adjacent to construction zones are isolated using sealed temporary plastic partitions, magnetic access zippers, negative air machines with HEPA filtration, and floor-to-floor protective carpet film. HVAC supply and return vents within the work zone are sealed to prevent circulating particulate through the rest of the house."
}, {
  id: "item-11",
  question: "How are kitchen layouts adapted around fixed plumbing stacks and load walls?",
  answer: "When working within vintage architecture, kitchen footprints can either be expanded by inserting recessed structural support headers flush into the ceiling joists, or optimized in-place using custom-depth casework, pantries with integrated mechanical chases, and relocated drain drops routed through crawlspaces or basements."
}, {
  id: "item-12",
  question: "What planning steps should be completed before site demolition begins?",
  answer: "Prior to demolition, initial spatial layouts and structural engineering calculations are finalized, building permits are secured, and all primary plumbing fixtures, tile selections, appliances, and bespoke cabinetry specifications are confirmed to prevent mid-project construction delays."
}];
export default function RemodelingFaq() {
  const leftColumnItems = FAQ_ITEMS.slice(0, 6);
  const rightColumnItems = FAQ_ITEMS.slice(6, 12);
  return <section id="decaturremodelingfaq" data-nav="light" className="bg-background text-foreground py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3.5 py-1 text-xs font-medium text-muted-foreground shadow-2xs backdrop-blur-xs">
            <HelpCircle className="size-3.5 text-chart-1" />
            <span>Planning & Regulations</span>
          </div>
          <h2 className="mt-4 font-serif text-3xl font-normal tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Decatur remodeling considerations
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Essential facts on permitting boundaries, historic design reviews, structural adaptations for older neighborhood homes, and project sequencing.
          </p>
        </div>

        {/* 12 Q&A Grid: Two balanced columns */}
        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          {/* Left Column (Items 1-6) */}
          <div className="flex flex-col gap-4">
            <Accordion type="single" collapsible className="flex flex-col gap-4">
              {leftColumnItems.map((item, index) => <AccordionItem key={item.id} value={item.id} data-index={index} className="rounded-xl border border-border bg-card/80 px-5 py-1.5 shadow-2xs transition-shadow duration-200 hover:shadow-xs last:border-b">
                  <AccordionTrigger className="font-serif text-base font-medium text-foreground hover:no-underline sm:text-lg text-left py-4">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="font-sans text-sm leading-relaxed text-muted-foreground pt-1 pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {/* Right Column (Items 7-12) */}
          <div className="flex flex-col gap-4">
            <Accordion type="single" collapsible className="flex flex-col gap-4">
              {rightColumnItems.map((item, index) => <AccordionItem key={item.id} value={item.id} data-index={index + 6} className="rounded-xl border border-border bg-card/80 px-5 py-1.5 shadow-2xs transition-shadow duration-200 hover:shadow-xs last:border-b">
                  <AccordionTrigger className="font-serif text-base font-medium text-foreground hover:no-underline sm:text-lg text-left py-4">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="font-sans text-sm leading-relaxed text-muted-foreground pt-1 pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>

        {/* Process & Planning Link Banner */}
        <div className="mt-14 rounded-2xl border border-border bg-secondary/60 p-6 text-center sm:flex sm:items-center sm:justify-between sm:p-8 sm:text-left">
          <div className="max-w-xl">
            <h3 className="font-serif text-xl font-normal text-secondary-foreground sm:text-2xl">
              Looking for a detailed step-by-step breakdown?
            </h3>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Review our pre-construction roadmap, phase milestones, and architectural design workflows.
            </p>
          </div>
          <div className="mt-5 sm:mt-0 sm:shrink-0">
            <Button asChild variant="default" className="rounded-md">
              <Link to="/ProcessPlanningPage" className="inline-flex items-center gap-2">
                <span>View planning process</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}